import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents } from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

const INTERVAL_CHECK_MS = 750;
const MIN_TOP_VALUE = 5;

/**
 * Tracks the user reaching the bottom of the page relative to a specific element.
 * Once that element is visible, we trigger the track event.
 * We track against a specific element because the absolute bottom of the page is not particularly meaningful for the average user.
 *
 * Normally, we have a helper function that calls track for cases where analytics needs to be called in redux.
 * HOWEVER, this particular tracking event is strictly tied to rendered content in the browser and needs to be a hook for a component to use.
 *
 * Generally, you should avoid using this directly, and use the `TrackPageBottomReachTrigger` component instead.
 *
 * @see `TrackPageBottomReachedTrigger` for the component to be placed at the bottom of your desired page
 * @returns ref for the element marked as the bottom of the page
 */
export const useTrackPageBottomReached = (): ReturnType<typeof useRef<HTMLDivElement>> => {
    const pageBottomRef = useRef<HTMLDivElement>(null);
    const { pathname, search } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    const hasTracked = useRef(false);

    const { isVisible } = useVisibilitySensor(pageBottomRef, {
        intervalCheck: INTERVAL_CHECK_MS,
        minTopValue: MIN_TOP_VALUE,
        partialVisibility: true,
        resizeCheck: true,
        scrollCheck: true,
    });

    const trackEvent = useCallback(() => {
        if (!hasTracked.current && isVisible) {
            track(TrackingEvents.PageBottomReached.PageBottomReached, {
                pageName: getAmplitudePageNameFromPath(pathname),
                url,
            });
            hasTracked.current = true;
        }
    }, [isVisible, pathname, url]);

    useEffect(() => {
        if (isVisible) {
            trackEvent();
        }
    }, [isVisible, trackEvent]);

    useEffect(() => {
        hasTracked.current = false;
    }, [search]);

    return pageBottomRef;
};
